import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';
import { toast } from 'sonner';
import { MarketIntelligence } from '../components/MarketIntelligence';
import { StudentWidgets } from '../components/StudentWidgets';

const ORANGE = ['#EA580C', '#F97316', '#FB923C', '#FDBA74', '#FED7AA', '#FFEDD5', '#C2410C'];

const Kpi = ({ label, value, testid, accent }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 border-t-4 border-t-orange-500 rounded-md p-5 card-hover stagger-in">
    <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">{label}</p>
    <p className={`font-display text-3xl font-extrabold tracking-tight ${accent || 'text-slate-900'}`}>{value ?? '-'}</p>
  </div>
);

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: d } = useQuery({ queryKey: ['dashboard'], queryFn: () => api.get('/dashboard').then((r) => r.data) });
  const role = user?.role;
  const { data: myBus } = useQuery({
    queryKey: ['my-bus'], queryFn: () => api.get('/transport/my-bus').then((r) => r.data),
    enabled: role === 'guardian', refetchInterval: 20000, retry: false,
  });
  const { data: upcoming } = useQuery({
    queryKey: ['upcoming-events'], queryFn: () => api.get('/events', { params: { upcoming: true } }).then((r) => r.data),
    retry: false,
  });

  const consentMut = useMutation({
    mutationFn: ({ sid, type, status }) => api.post(`/students/${sid}/consents`, { type, status }),
    onSuccess: (r) => { toast.success(`Consent ${r.data.status} - the school and audit log reflect this immediately`); qc.invalidateQueries({ queryKey: ['dashboard'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const digestMut = useMutation({
    mutationFn: () => api.post('/digest/weekly/send'),
    onSuccess: (r) => toast.success(`Digest sent to ${r.data.recipients ?? r.data.sent} recipient(s) - email: ${r.data.email_status || 'see log'}`),
    onError: (e) => toast.error(errMsg(e)),
  });
  const download = async (fmt) => {
    try {
      const res = await api.get(`/digest/weekly.${fmt}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `weekly-digest.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const pipelineData = Object.entries(d?.pipeline || {}).map(([stage, n]) => ({ stage, n }));
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div data-testid="dashboard-page">
      <div className="bg-white border border-slate-200 rounded-md p-5 sm:p-6 mb-8 relative overflow-hidden stagger-in" data-testid="dashboard-welcome-header">
        <div className="absolute inset-y-0 left-0 w-1.5 bg-orange-500" />
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono-label text-[10px] uppercase text-orange-600 mb-1">{today} · {role?.replace(/_/g, ' ')}</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{greeting()}, {user?.name?.split(' ')[0]} 👋</h1>
            <p className="text-sm text-slate-500 mt-1">{t('Welcome')} to your ONRID workspace - know everyone, trust the record.</p>
          </div>
          <div className="hidden sm:block shrink-0">
            <img src="/brand/onrid-banner-transparent.png" alt="ONRID" className="h-14 w-auto object-contain" data-testid="dashboard-brand-banner" />
          </div>
        </div>
      </div>

      {['global_admin', 'school_admin', 'principal', 'registrar'].includes(role) && !d && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="dashboard-skeleton">
          {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-24 rounded-md" />)}
        </div>
      )}
      {['global_admin', 'school_admin', 'principal', 'registrar'].includes(role) && d && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Kpi label="Students" value={d.students} testid="kpi-students" />
            <Kpi label="Staff" value={d.staff} testid="kpi-staff" />
            <Kpi label="Applications" value={d.applications} testid="kpi-applications" />
            <Kpi label="Arrears (₹)" value={d.arrears_total?.toLocaleString()} testid="kpi-arrears" accent="text-rose-600" />
          </div>
          <MarketIntelligence />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-md p-5 md:col-span-2 stagger-in">
              <h3 className="font-display font-semibold mb-1">Admissions pipeline</h3>
              <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">live stage counts</p>
              <div className="h-52" data-testid="pipeline-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipelineData} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                    <XAxis dataKey="stage" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={{ stroke: '#E2E8F0' }} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: '#FFF7ED' }} contentStyle={{ borderRadius: 8, border: '1px solid #FED7AA', fontSize: 12 }} />
                    <Bar dataKey="n" radius={[4, 4, 0, 0]}>
                      {pipelineData.map((d, i) => <Cell key={d.stage} fill={ORANGE[i % ORANGE.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {pipelineData.map(({ stage, n }) => (
                  <Link to="/admissions" key={stage} className="border border-slate-200 rounded-md px-3 py-1.5 hover:border-orange-500 transition-colors text-xs">
                    <span className="font-mono-label text-[9px] uppercase text-slate-400 mr-1.5">{stage}</span>
                    <span className="font-display font-bold text-orange-700">{n}</span>
                  </Link>
                ))}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-5 stagger-in">
              <h3 className="font-display font-semibold mb-1">Needs attention</h3>
              <ul className="space-y-3 text-sm mt-3">
                <li className="flex justify-between"><span>Attendance conflicts</span><span className="font-bold text-amber-600" data-testid="attn-conflicts">{d.pending_conflicts}</span></li>
                <li className="flex justify-between"><span>AI drafts pending approval</span><span className="font-bold text-orange-600" data-testid="attn-drafts">{d.pending_drafts}</span></li>
                <li className="flex justify-between"><span>Break-glass reviews</span><span className="font-bold text-rose-600" data-testid="attn-breakglass">{d.break_glass_pending}</span></li>
              </ul>
              {['global_admin', 'school_admin', 'principal'].includes(role) && (
                <div className="border-t border-slate-100 mt-4 pt-3" data-testid="digest-card">
                  <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Weekly digest</p>
                  <div className="flex gap-2 flex-wrap">
                    <button data-testid="digest-pdf-button" onClick={() => download('pdf')} className="text-xs border border-slate-200 rounded px-2.5 py-1.5 hover:border-orange-500 transition-colors">Download PDF</button>
                    <button data-testid="digest-xlsx-button" onClick={() => download('xlsx')} className="text-xs border border-slate-200 rounded px-2.5 py-1.5 hover:border-orange-500 transition-colors">Download Excel</button>
                    <button data-testid="digest-send-button" onClick={() => digestMut.mutate()} className="text-xs bg-orange-600 text-white rounded px-2.5 py-1.5 hover:bg-orange-500 transition-colors">Email now</button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2">Auto-emailed to principals every Monday 8:30am IST</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {role === 'admissions_officer' && d && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Total applications" value={d.applications} testid="kpi-applications" />
          {Object.entries(d.pipeline || {}).map(([stage, n]) => <Kpi key={stage} label={stage} value={n} testid={`kpi-${stage}`} />)}
        </div>
      )}

      {role === 'finance_admin' && d && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Kpi label="Collected (₹)" value={d.collected?.toLocaleString()} testid="kpi-collected" accent="text-green-700" />
          <Kpi label="Arrears (₹)" value={d.arrears_total?.toLocaleString()} testid="kpi-arrears" accent="text-rose-600" />
          <Kpi label="Invoices" value={d.invoices} testid="kpi-invoices" />
        </div>
      )}

      {role === 'teacher' && d && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Kpi label="My classes" value={d.classes} testid="kpi-classes" />
          <Kpi label="Attendance conflicts" value={d.pending_conflicts} testid="kpi-conflicts" accent="text-amber-600" />
          <Kpi label="My drafts pending" value={d.pending_drafts} testid="kpi-drafts" accent="text-orange-600" />
        </div>
      )}

      {role === 'student' && d?.student && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 border-t-4 border-t-orange-500 rounded-md p-5 stagger-in" data-testid="student-identity-card">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">My identity</p>
            <p className="font-display text-xl font-bold">{d.student.name}</p>
            <p className="text-sm text-slate-500">Grade {d.student.grade}{d.student.section} · {d.student.admission_no}</p>
          </div>
          <Kpi label="Attendance %" value={d.attendance_pct} testid="kpi-attendance" accent="text-green-700" />
          <Kpi label="Released report cards" value={d.released_reports} testid="kpi-reports" />
        </div>
      )}

      {['student', 'guardian'].includes(role) && <StudentWidgets />}

      {(upcoming || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-4 stagger-in" data-testid="upcoming-events-card">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Upcoming events</p>
          <div className="flex flex-wrap gap-2">
            {upcoming.slice(0, 4).map((e) => (
              <Link key={e.id} to="/events" className="border border-slate-200 rounded-md px-3 py-1.5 text-xs hover:border-orange-500 transition-colors" data-testid={`upcoming-event-${e.id}`}>
                <span className="font-mono-label text-[9px] uppercase text-orange-600 me-1.5">{e.date.slice(5)}</span>
                <span className="font-medium">{e.title}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {role === 'guardian' && (myBus || []).length > 0 && (
        <div className="bg-white border border-slate-200 border-t-4 border-t-orange-500 rounded-md p-5 mb-4 stagger-in" data-testid="live-bus-card">
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">Live school bus · updates every 20s</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {myBus.map((b) => (
              <div key={`${b.student_id}-${b.route_name}`} className="border border-slate-100 rounded-md p-3" data-testid={`bus-status-${b.student_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-sm">{b.student_name}</span>
                  <span className="text-xs text-slate-400">{b.route_name}{b.vehicle_reg ? ` · ${b.vehicle_reg}` : ''}</span>
                  <span className={`ms-auto font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${b.online ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`} data-testid={`bus-online-${b.student_id}`}>{b.online ? '● bus live' : 'bus offline'}</span>
                </div>
                <div className="mt-2 text-sm">
                  {b.online && b.eta_min !== null && b.eta_min !== undefined ? (
                    <p data-testid={`bus-eta-${b.student_id}`}>
                      Arriving at <span className="font-semibold">{b.stop || 'stop'}</span> in{' '}
                      <span className="font-display font-extrabold text-orange-600 text-lg">{b.eta_min <= 1 ? '≤1 min' : `~${b.eta_min} min`}</span>
                      {b.position?.near && <span className="text-xs text-slate-400"> · now near {b.position.near}</span>}
                    </p>
                  ) : b.online ? (
                    <p className="text-slate-500 text-xs">Bus is on the road{b.position?.near ? ` - near ${b.position.near}` : ''}. Stop location pending for ETA.</p>
                  ) : (
                    <p className="text-slate-400 text-xs">No GPS signal right now - pickup at {b.stop || 'assigned stop'}{b.stop_time ? ` around ${b.stop_time}` : ''}.</p>
                  )}
                  {b.position && (
                    <a href={`https://www.openstreetmap.org/?mlat=${b.position.lat}&mlon=${b.position.lng}#map=16/${b.position.lat}/${b.position.lng}`} target="_blank" rel="noreferrer"
                      className="text-xs text-orange-700 underline" data-testid={`bus-map-link-${b.student_id}`}>view on map</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {role === 'guardian' && d && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(d.children || []).map((c) => (
            <div key={c.id} data-testid={`child-card-${c.id}`} className="bg-white border border-slate-200 border-t-4 border-t-orange-500 rounded-md p-5 card-hover stagger-in">
              <Link to={`/students/${c.id}`} className="block">
                <p className="font-display text-xl font-bold hover:underline">{c.name}</p>
                <p className="text-sm text-slate-500 mb-3">Grade {c.grade}{c.section} · {c.admission_no}</p>
              </Link>
              <div className="flex gap-2 flex-wrap mb-4">
                <span className={`font-mono-label text-[9px] uppercase px-2 py-1 rounded ${c.access.can_view_academics ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>academics {c.access.can_view_academics ? 'on' : 'off'}</span>
                <span className={`font-mono-label text-[9px] uppercase px-2 py-1 rounded ${c.access.can_view_fees ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>fees {c.access.can_view_fees ? 'on' : 'off'}</span>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">{t('Consents you control')}</p>
                {['ai_assistant', 'data_processing'].map((type) => {
                  const consent = (c.consents || []).find((x) => x.type === type);
                  const granted = consent?.status === 'granted';
                  return (
                    <div key={type} className="flex items-center justify-between py-1.5 text-sm">
                      <span className="capitalize">{type === 'ai_assistant' ? t('AI learning assistant') : t('Data processing (DPDP)')}</span>
                      <div className="flex items-center gap-2">
                        <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${granted ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-700'}`} data-testid={`consent-status-${type}-${c.id}`}>{consent?.status || 'not set'}</span>
                        <button data-testid={`consent-toggle-${type}-${c.id}`}
                          onClick={() => {
                            if (granted && !window.confirm('Withdraw this consent? The change takes effect immediately and is audited.')) return;
                            consentMut.mutate({ sid: c.id, type, status: granted ? 'withdrawn' : 'granted' });
                          }}
                          className={`text-xs rounded px-2 py-1 transition-colors ${granted ? 'border border-rose-200 text-rose-600 hover:bg-rose-50' : 'bg-green-700 text-white hover:bg-green-600'}`}>
                          {granted ? t('Withdraw') : t('Grant')}
                        </button>
                      </div>
                    </div>
                  );
                })}
                <p className="text-[10px] text-slate-400 mt-1">{t('Withdrawing AI consent hides the assistant from your child instantly.')}</p>
              </div>
            </div>
          ))}
          {(d.children || []).length === 0 && <p className="text-slate-500">No linked children visible. If access is blocked, contact the school registrar.</p>}
        </div>
      )}
    </div>
  );
}
